import { Field } from "@base-ui/react/field";
import { Input as BaseInput } from "@base-ui/react/input";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";
import { vars } from "../../theme/theme.css";
import { FieldMessage } from "../FieldInfo/FieldMessage";
import { IconExclamation } from "../icons";
import { Spinner } from "../Spinner";
import {
  textAreaClass,
  textAreaContainerClass,
  textAreaVariants,
} from "./styles.css";

export type TextAreaVariant = "default" | "filled";

interface TextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string | React.ReactNode;
  info?: string | React.ReactNode;
  error?: string;
  containerStyles?: React.CSSProperties;
  isLoading?: boolean;
  variant?: TextAreaVariant;
}

export const TextArea = forwardRef(function TextArea(
  {
    label,
    info,
    error,
    containerStyles,
    isLoading,
    variant = "default",
    rows,
    cols,
    wrap,
    className,
    ...props
  }: TextAreaProps,
  ref: React.Ref<HTMLElement>,
) {
  const isDisabled = props.disabled || isLoading;

  return (
    <Field.Root
      className={textAreaContainerClass}
      style={containerStyles}
      invalid={!!error}
    >
      {label && <Field.Label>{label}</Field.Label>}
      <BaseInput
        render={<textarea rows={rows} cols={cols} wrap={wrap} />}
        {...(props as unknown as React.ComponentProps<typeof BaseInput>)}
        disabled={isDisabled}
        className={clsx(textAreaClass, textAreaVariants[variant], className)}
        style={{
          paddingRight: isLoading || error ? "40px" : undefined,
          ...props.style,
        }}
        ref={(e: HTMLElement | null) => {
          if (ref) {
            //@ts-expect-error
            ref.current = e;
          }
        }}
      />
      <FieldMessage error={error} info={info} isDisabled={isDisabled} />
      {isLoading && (
        <Spinner
          color={vars.colors.neutral.light}
          style={{
            position: "absolute",
            right: 16,
            top: 36,
          }}
        />
      )}
      {error && (
        <IconExclamation
          style={{ position: "absolute", right: 16, top: 36 }}
          fill={vars.colors.red.default}
        />
      )}
    </Field.Root>
  );
});

TextArea.displayName = "TextArea";
